import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff, Gift } from "lucide-react";
import { api, errMsg, resolveImg, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SectionTitle, Field, Empty } from "@/admin/ui";
import { ImagePicker, MultiImagePicker } from "@/admin/ImagePicker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const EMPTY = { title: "", category: "", preview: "", content: "", price: 0, is_free: false, image_url: "", content_images: [], extra_buyers: 0, extra_viewers: 0, active: true, order: 0 };

export function AdminInfos() {
  const { site } = useAuth();
  const [infos, setInfos] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/infos").then((r) => setInfos(r.data)).catch(() => setInfos([])), []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ ...EMPTY, order: (infos?.length || 0) + 1 }); setEditing("new"); };
  const openEdit = (i) => { setForm({ ...EMPTY, ...i, content_images: i.content_images || [] }); setEditing(i.id); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = { ...form, price: Number(form.price) || 0, extra_buyers: Number(form.extra_buyers) || 0, extra_viewers: Number(form.extra_viewers) || 0, order: Number(form.order) || 0 };
    ["id", "created_at", "updated_at", "buyers_count"].forEach((k) => delete body[k]);
    try {
      if (editing === "new") await api.post("/admin/infos", body); else await api.put(`/admin/infos/${editing}`, body);
      toast.success("Kaydedildi");
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };

  const remove = async (i) => {
    if (!window.confirm(`"${i.title}" silinsin mi?`)) return;
    try { await api.delete(`/admin/infos/${i.id}`); toast.success("Silindi"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div>
      <SectionTitle eyebrow="İçerik Yönetimi" title="Bilgi Paketleri">
        <Button data-testid="admin-add-info-button" onClick={openNew} className="cx-btn-primary rounded-xl h-10"><Plus className="h-4 w-4 mr-1" /> Yeni Bilgi Ekle</Button>
      </SectionTitle>
      {infos === null ? <div className="h-40 rounded-2xl bg-slate-900/60 animate-pulse" /> : infos.length === 0 ? <Empty text="Henüz bilgi eklenmemiş. 'Yeni Bilgi Ekle' ile başla." /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="admin-infos-list">
          {infos.map((i) => (
            <div key={i.id} data-testid="admin-info-item" className="cx-glass rounded-2xl overflow-hidden flex flex-col">
              <div className="aspect-[16/9] bg-slate-900 relative">
                {i.image_url && <img src={resolveImg(i.image_url)} alt="" className="h-full w-full object-cover" />}
                <span className={`absolute top-3 left-3 text-[10px] font-mono-cx px-2 py-1 rounded-md border ${i.active ? "bg-emerald-950 text-emerald-300 border-emerald-500/50" : "bg-slate-900 text-slate-400 border-slate-700"}`}>{i.active ? <><Eye className="h-3 w-3 inline mr-1" />YAYINDA</> : <><EyeOff className="h-3 w-3 inline mr-1" />GİZLİ</>}</span>
                <span className="absolute top-3 right-3 text-[10px] font-mono-cx px-2 py-1 rounded-md bg-slate-950/80 border border-slate-700 text-slate-300">#{i.order}</span>
                {i.is_free && <span className="absolute bottom-3 left-3 text-[10px] font-bold px-2 py-1 rounded-md bg-emerald-500 text-[#05070D]">ÜCRETSİZ</span>}
              </div>
              <div className="p-4 flex-1 flex flex-col gap-2">
                <h3 className="font-display font-semibold text-slate-50">{i.title}</h3>
                <p className="text-xs text-slate-500 line-clamp-2">{i.preview}</p>
                <div className="flex items-center justify-between text-xs text-slate-400 mt-auto pt-2">
                  <span>{i.buyers_count} satın alma · +{i.extra_viewers} izleyici · {(i.content_images || []).length} foto</span>
                  <span className="font-display font-bold text-cyan-300">{i.is_free ? "Ücretsiz" : fmtMoney(i.price, site?.currency)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button data-testid="admin-edit-info-button" variant="outline" onClick={() => openEdit(i)} className="h-9 rounded-lg bg-slate-900 border-slate-700 text-slate-200 hover:bg-slate-800"><Pencil className="h-3.5 w-3.5 mr-1" /> Düzenle</Button>
                  <Button data-testid="admin-delete-info-button" variant="outline" onClick={() => remove(i)} className="h-9 rounded-lg bg-red-950/50 border-red-500/40 text-red-300 hover:bg-red-950"><Trash2 className="h-3.5 w-3.5 mr-1" /> Sil</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-slate-950/95 backdrop-blur-xl border-slate-800 text-slate-100 sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-info-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing === "new" ? "Yeni Bilgi Paketi" : "Bilgiyi Düzenle"}</DialogTitle>
            <DialogDescription className="text-slate-400">Başlık, önizleme, gizli içerik, fiyat ve görselleri buradan ayarla. Kaydettiğinde siteye otomatik yansır.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Başlık"><Input data-testid="info-form-title" className="cx-input" value={form.title} onChange={set("title")} required /></Field>
              <Field label="Kategori (etiket)"><Input data-testid="info-form-category" className="cx-input" value={form.category} onChange={set("category")} placeholder="VIP, ÖZEL, VERİ..." /></Field>
            </div>
            <Field label="Önizleme Metni (herkes görür)"><Textarea data-testid="info-form-preview" className="cx-input min-h-[80px] h-auto" value={form.preview} onChange={set("preview")} /></Field>
            <Field label="Gizli İçerik (sadece satın alan görür)"><Textarea data-testid="info-form-content" className="cx-input min-h-[140px] h-auto" value={form.content} onChange={set("content")} /></Field>
            <Field label="Gizli İçerik Fotoğrafları (satın alan görür)">
              <MultiImagePicker values={form.content_images} onChange={(v) => setForm((f) => ({ ...f, content_images: v }))} testId="info-form-content-images" />
            </Field>

            <div className="flex items-center justify-between rounded-xl bg-emerald-950/30 border border-emerald-500/30 px-4 py-3">
              <span className="text-sm text-emerald-200 inline-flex items-center gap-2"><Gift className="h-4 w-4" /> Ücretsiz bilgi (ödeme istenmez, herkes içeriği görür)</span>
              <Switch data-testid="info-form-free" checked={!!form.is_free} onCheckedChange={(v) => setForm((f) => ({ ...f, is_free: v }))} />
            </div>

            <div className="grid sm:grid-cols-4 gap-4">
              <Field label={`Ücret (${site?.currency || "₺"})`}><Input data-testid="info-form-price" type="number" step="0.01" className="cx-input" value={form.price} onChange={set("price")} disabled={form.is_free} /></Field>
              <Field label="Ek Satın Alan"><Input data-testid="info-form-extra-buyers" type="number" className="cx-input" value={form.extra_buyers} onChange={set("extra_buyers")} /></Field>
              <Field label="Ek İzleyici"><Input data-testid="info-form-extra-viewers" type="number" className="cx-input" value={form.extra_viewers} onChange={set("extra_viewers")} /></Field>
              <Field label="Sıra"><Input data-testid="info-form-order" type="number" className="cx-input" value={form.order} onChange={set("order")} /></Field>
            </div>
            <Field label="Kart Fotoğrafı">
              <ImagePicker value={form.image_url} onChange={(v) => setForm((f) => ({ ...f, image_url: v }))} testId="info-form-image" />
            </Field>
            <div className="flex items-center justify-between rounded-xl bg-slate-900/60 border border-slate-800 px-4 py-3">
              <span className="text-sm text-slate-300">Sitede yayında</span>
              <Switch data-testid="info-form-active" checked={!!form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
            </div>
            <Button data-testid="info-form-save" type="submit" disabled={saving} className="w-full cx-btn-primary rounded-xl h-11">{saving ? "Kaydediliyor..." : "Kaydet"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
